import { isAxiosError } from "axios";
import { api, errorMessage } from "./api";
import type { OzoZaduzenje } from "./radnici";

export type EquipmentMode = "osobna" | "potrosna" | "zaliha";
export const SIZE_FIELDS = {
  gornji_dio: "Gornji dio",
  donji_dio: "Donji dio",
  obuca: "Obuća",
  kaciga: "Kaciga",
  rukavice: "Rukavice",
  zastitni_pojas: "Zaštitni pojas",
  prsluk: "Prsluk",
} as const;
export interface EquipmentType {
  id: string;
  naziv: string;
  nacin: EquipmentMode;
  kvota_12mj: number | null;
  polje_velicine: keyof typeof SIZE_FIELDS | null;
  zadani_rok_mjeseci: number | null;
  aktivna: boolean;
}
export interface EquipmentProposal {
  vrsta_ozo_id: string;
  naziv: string;
  nacin: EquipmentMode;
  velicina: string | null;
  kvota_12mj: number | null;
  iskoristeno_12mj: number;
  zadnje_izdano: string | null;
  aktivno_zaduzenje: {
    id: string;
    rok_zamjene: string | null;
    dana_do_roka: number | null;
  } | null;
  predlozeno: boolean;
  potrosnja_mjesec: number | null;
  zaliha_gradilista: number | null;
}
export interface HandoverItem {
  vrsta_ozo_id: string;
  velicina?: string | null;
  kolicina?: number;
  rok_zamjene?: string | null;
  oznaka?: string | null;
  razlog_izvan_kvote?: string | null;
  vratio_staro?: boolean;
  iz_zalihe?: boolean;
  iz_ureda?: boolean;
}
export interface HandoverEvidence {
  photos: File[];
  signature: File | null;
  noPhotoReason: string;
}
export const emptyEvidence = (): HandoverEvidence => ({
  photos: [],
  signature: null,
  noPhotoReason: "",
});
export interface HandoverInput {
  vrsta: "izdavanje" | "pocetno_stanje";
  gradiliste_id?: string;
  datum: string;
  stavke: HandoverItem[];
  napomena?: string;
}
export interface HandoverReceipt {
  id: string;
  vrsta: "izdavanje" | "pocetno_stanje" | "zamjena" | "vracanje";
  datum: string;
  gradiliste: { id: string; naziv: string } | null;
  izdao: { id: string; naziv: string };
  dokument_id: string | null;
  bez_fotke: boolean;
  stavke: OzoZaduzenje[];
  operation_id: string;
  undo_until: string;
}
export interface HandoverNotice {
  workerId: string;
  receipt: HandoverReceipt;
}
export interface SiteEquipmentOverview {
  plocice: {
    istice_14: number;
    fali_velicina: number;
    nije_popisano: number;
    rukavice_mjesec: number;
    bez_fotke: number;
  };
  treba_rijesiti: {
    radnik_id: string;
    ime: string;
    vrsta: string;
    poruka: string;
    akcija: "izdaj" | "upisi_velicinu" | "uredi" | "popisi";
  }[];
  nedavno: {
    id: string;
    radnik_id: string;
    ime: string;
    datum: string;
    broj_stavki: number;
    ima_fotku: boolean;
    dokument_id?: string | null;
  }[];
  potrosnja_rukavica: {
    radnik_id: string;
    ime: string;
    tekuci_mjesec: number;
    prosli_mjesec: number;
  }[];
}
export interface InventoryWorker {
  radnik_id: string;
  ime: string;
  status: "popisan" | "nije_popisan";
  datum: string | null;
  stavke: OzoZaduzenje[];
}
export interface SiteStockItem {
  vrsta_ozo_id: string;
  naziv: string;
  stanje: number;
  nevraceno_danas: { radnik_id: string; ime: string; kolicina: number }[];
}
export type SiteStock = SiteStockItem[];
export interface StockMovement {
  vrsta_prometa: "izdano" | "vraceno" | "primitak";
  vrsta_ozo_id: string;
  kolicina: number;
  radnik_id?: string;
  napomena?: string;
}
export const equipmentApi = {
  proposal: async (worker: string, site?: string, signal?: AbortSignal) =>
    (
      await api.get<EquipmentProposal[]>(
        `/radnici/${worker}/zastitna-oprema/prijedlog`,
        { params: site ? { gradiliste_id: site } : undefined, signal },
      )
    ).data,
  overview: async (site: string, signal?: AbortSignal) =>
    (
      await api.get<SiteEquipmentOverview>(
        `/gradilista/${site}/oprema/pregled`,
        { signal },
      )
    ).data,
  inventory: async (site: string, signal?: AbortSignal) =>
    (
      await api.get<InventoryWorker[]>(
        `/gradilista/${site}/oprema/pocetno-stanje`,
        { signal },
      )
    ).data,
  stock: async (site: string, signal?: AbortSignal) =>
    (await api.get<SiteStock>(`/gradilista/${site}/oprema/zaliha`, { signal }))
      .data,
  move: async (site: string, input: StockMovement) =>
    (await api.post(`/gradilista/${site}/oprema/zaliha/promet`, input)).data,
  types: async (signal?: AbortSignal) =>
    (await api.get<EquipmentType[]>("/sifrarnici/vrste-opreme", { signal }))
      .data,
  updateType: async (id: string, input: Omit<EquipmentType, "id">) =>
    (await api.put<EquipmentType>(`/sifrarnici/vrste-opreme/${id}`, input))
      .data,
  issue: async (
    worker: string,
    input: HandoverInput,
    evidence: HandoverEvidence,
    onProgress?: (percent: number) => void,
  ) => {
    const body = evidenceForm(evidence);
    body.set("vrsta", input.vrsta);
    body.set("datum", input.datum);
    body.set("stavke", JSON.stringify(input.stavke));
    if (input.gradiliste_id) body.set("gradiliste_id", input.gradiliste_id);
    if (input.napomena) body.set("napomena", input.napomena);
    return (
      await api.post<HandoverReceipt>(
        `/radnici/${worker}/zastitna-oprema/primopredaja`,
        body,
        {
          onUploadProgress: (event) =>
            onProgress?.(
              event.total ? Math.round((event.loaded / event.total) * 100) : 0,
            ),
        },
      )
    ).data;
  },
  undo: async (worker: string, operation: string) =>
    api.post(
      `/radnici/${worker}/zastitna-oprema/primopredaja/${operation}/ponisti`,
    ),
};
export function evidenceForm(evidence: HandoverEvidence): FormData {
  const body = new FormData();
  evidence.photos.forEach((photo) => body.append("fotke[]", photo, photo.name));
  if (evidence.signature)
    body.set("potpis", evidence.signature, evidence.signature.name);
  if (!evidence.photos.length && evidence.noPhotoReason.trim())
    body.set("bez_fotke_razlog", evidence.noPhotoReason.trim());
  return body;
}
export function evidenceReady(
  evidence: HandoverEvidence,
  canSkipPhoto: boolean,
): boolean {
  return Boolean(
    evidence.signature &&
    (evidence.photos.length || (canSkipPhoto && evidence.noPhotoReason.trim())),
  );
}
export function issueUrl(worker: string, site?: string): string {
  const params = new URLSearchParams({ radnik: worker });
  if (site) params.set("gradiliste", site);
  return `/oprema/izdaj?${params}`;
}
export function documentUrl(worker: string, document: string): string {
  return `/zaposlenici/${encodeURIComponent(worker)}?tab=dokumenti&document=${encodeURIComponent(document)}`;
}
export interface EquipmentProofFile {
  id: string;
  employee_document_id: string;
  dokument_id: string;
}
export function equipmentProofPath(worker: string, document?: string): string {
  const base = `/radnici/${encodeURIComponent(worker)}/zastitna-oprema/potvrde`;
  return document ? `${base}/${encodeURIComponent(document)}` : base;
}
export function equipmentFieldErrors(cause: unknown): Record<string, string> {
  if (!isAxiosError(cause)) return {};
  const detail: unknown = cause.response?.data?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    "polje" in detail &&
    typeof detail.polje === "string" &&
    "poruka" in detail &&
    typeof detail.poruka === "string"
  )
    return {
      ["vrsta_ozo_id" in detail && typeof detail.vrsta_ozo_id === "string"
        ? `${detail.vrsta_ozo_id}.${detail.polje}`
        : detail.polje]: detail.poruka,
    };
  if (Array.isArray(detail))
    return Object.fromEntries(
      detail
        .filter((item): item is { loc: (string | number)[]; msg: string } =>
          Boolean(
            item && Array.isArray(item.loc) && typeof item.msg === "string",
          ),
        )
        .map((item) => [
          item.loc.filter((part) => part !== "body").join("."),
          item.msg.replace(/^Value error,\s*/, ""),
        ]),
    );
  if (
    detail &&
    typeof detail === "object" &&
    "field" in detail &&
    typeof detail.field === "string"
  )
    return {
      [detail.field]: errorMessage(cause, "Provjerite unesene podatke."),
    };
  return {};
}
export function equipmentErrorMessage(
  cause: unknown,
  fallback: string,
): string {
  if (isAxiosError(cause)) {
    const detail: unknown = cause.response?.data?.detail;
    if (
      detail &&
      typeof detail === "object" &&
      "poruka" in detail &&
      typeof detail.poruka === "string"
    )
      return detail.poruka;
  }
  return errorMessage(cause, fallback);
}
